/**
 * Feature Suggestion Dismissal Service
 *
 * Manages the persistence of feature suggestion dismissal counts in browser localStorage.
 * This service tracks how many times a user has dismissed specific feature suggestions,
 * allowing for automatic disabling of suggestions after a defined threshold.
 *
 */

// Utils
import localStorageService from '../../utils/services/localStorage/localStorage';

const FEATURE_SUGGESTION_DISMISS_COUNT_KEY_PREFIX = 'milanote.featureSuggestion.dismissCount';

const getDismissCountKey = (feature: string): string => `${FEATURE_SUGGESTION_DISMISS_COUNT_KEY_PREFIX}.${feature}`;

/**
 * Safely parse a dismiss count value from localStorage, defaulting to 0 for invalid values
 */
const parseDismissCount = (value: string | null): number => {
    if (value === null || value === undefined) return 0;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : Math.max(parsed, 0);
};

/**
 * Get the current dismiss count for a feature suggestion
 * @param feature - The feature identifier
 * @returns The number of times the suggestion has been dismissed (0 if never dismissed)
 */
export const getFeatureSuggestionDismissCount = (feature: string): number => {
    if (!feature) return 0;
    const key = getDismissCountKey(feature);
    return parseDismissCount(localStorageService.getItem(key));
};

/**
 * Set the dismiss count for a feature suggestion
 * @param feature - The feature identifier
 * @param count - The dismiss count to set (will be clamped to non-negative values)
 */
export const setFeatureSuggestionDismissCount = (feature: string, count: number): void => {
    if (!feature) return;
    const key = getDismissCountKey(feature);
    localStorageService.setItem(key, String(Math.max(Number(count) || 0, 0)));
};

/**
 * Increment the dismiss count for a feature suggestion by 1
 * @param feature - The feature identifier
 * @returns The new dismiss count after incrementing
 */
export const incrementFeatureSuggestionDismissCount = (feature: string): number => {
    const nextCount = getFeatureSuggestionDismissCount(feature) + 1;
    setFeatureSuggestionDismissCount(feature, nextCount);
    return nextCount;
};

/**
 * Reset the dismiss count for a feature suggestion to 0
 * @param feature - The feature identifier
 */
export const resetFeatureSuggestionDismissCount = (feature: string): void => {
    setFeatureSuggestionDismissCount(feature, 0);
};
